import type { FitmentCatalogSettings, FitmentFieldDefinition } from '../models/FitmentCatalog';
import type { Vehicle } from '../models/Vehicle';

export type LegacyFitmentKey = 'year' | 'make' | 'model' | 'submodel';

// Selector state is stored by normalized field key so automotive and custom
// catalogs can share the same UI/API path.
export type FitmentFieldValues = Record<string, string>;

// Fallback used before catalog settings load, or for older shops that have not
// saved the new selector-field configuration yet.
export const defaultFitmentFields: FitmentFieldDefinition[] = [
    {
        fieldIndex: 1,
        key: 'year',
        label: 'Year',
        dataType: 'Text',
        isEnabled: true,
        isRequired: true,
        isFilterable: true,
        isVisible: true,
        sortDirection: 'Desc',
        displayOrder: 1,
        maxLength: 200,
    },
    {
        fieldIndex: 2,
        key: 'make',
        label: 'Make',
        dataType: 'Text',
        isEnabled: true,
        isRequired: true,
        isFilterable: true,
        isVisible: true,
        sortDirection: 'Asc',
        displayOrder: 2,
        maxLength: 200,
    },
    {
        fieldIndex: 3,
        key: 'model',
        label: 'Model',
        dataType: 'Text',
        isEnabled: true,
        isRequired: true,
        isFilterable: true,
        isVisible: true,
        sortDirection: 'Asc',
        displayOrder: 3,
        maxLength: 200,
    },
    {
        fieldIndex: 4,
        key: 'submodel',
        label: 'Submodel',
        dataType: 'Text',
        isEnabled: true,
        isRequired: false,
        isFilterable: true,
        isVisible: true,
        sortDirection: 'Asc',
        displayOrder: 4,
        maxLength: 200,
    },
];

const legacyKeyByIndex: Record<number, LegacyFitmentKey> = {
    1: 'year',
    2: 'make',
    3: 'model',
    4: 'submodel',
};

const legacyFieldByKey: Record<LegacyFitmentKey, keyof Vehicle> = {
    year: 'field1',
    make: 'field2',
    model: 'field3',
    submodel: 'field4',
};

export const normalizeFitmentKey = (field: Pick<FitmentFieldDefinition, 'key' | 'fieldIndex'>): string =>
    (field.key || legacyKeyByIndex[field.fieldIndex] || '').trim().toLowerCase();

export const isLegacyFitmentKey = (key: string): key is LegacyFitmentKey =>
    key === 'year' || key === 'make' || key === 'model' || key === 'submodel';

export const getCatalogFields = (catalog?: FitmentCatalogSettings | null): FitmentFieldDefinition[] => {
    const fields = catalog?.fields?.length ? catalog.fields : defaultFitmentFields;

    return [...fields].sort((left, right) =>
        (left.displayOrder || left.fieldIndex) - (right.displayOrder || right.fieldIndex)
        || left.fieldIndex - right.fieldIndex);
};

export const getVisibleFitmentFields = (catalog?: FitmentCatalogSettings | null): FitmentFieldDefinition[] =>
    getCatalogFields(catalog).filter((field) => field.isEnabled && field.isVisible);

export const getFilterableFitmentFields = (catalog?: FitmentCatalogSettings | null): FitmentFieldDefinition[] =>
    getCatalogFields(catalog).filter((field) => field.isEnabled && field.isFilterable);

export const getLegacyVehicleFieldValue = (vehicle: Vehicle, field: FitmentFieldDefinition): string => {
    const explicitKey = (field.key || '').trim().toLowerCase();
    const key = explicitKey || legacyKeyByIndex[field.fieldIndex] || '';

    // Newer APIs return fieldValues for any merchant-defined selector field.
    if (vehicle.fieldValues && vehicle.fieldValues[key] != null) {
        return vehicle.fieldValues[key];
    }

    // Legacy rows still expose the first four fields as field1-field4.
    if (isLegacyFitmentKey(key)) {
        return (vehicle[legacyFieldByKey[key]] as string) || '';
    }

    // Old stores may not have field keys yet, so only keyless fields fall back
    // to the historical field1-field4 shape.
    if (!explicitKey) {
        const fallbackKey = legacyKeyByIndex[field.fieldIndex];
        return fallbackKey ? ((vehicle[legacyFieldByKey[fallbackKey]] as string) || '') : '';
    }

    return '';
};

export const getLegacyFieldStateKey = (field: FitmentFieldDefinition): LegacyFitmentKey | null => {
    const explicitKey = (field.key || '').trim().toLowerCase();
    const key = explicitKey || legacyKeyByIndex[field.fieldIndex] || '';
    if (isLegacyFitmentKey(key)) return key;
    return null;
};

export const getFitmentFieldValue = (values: FitmentFieldValues, field: FitmentFieldDefinition): string =>
    values[normalizeFitmentKey(field)] || '';

export const hasAnyFitmentFieldValue = (fields: FitmentFieldDefinition[], values: FitmentFieldValues): boolean =>
    fields.some((field) => !!getFitmentFieldValue(values, field));

export const hasRequiredFitmentFieldValues = (fields: FitmentFieldDefinition[], values: FitmentFieldValues): boolean =>
    // "Require value" means all required fields must be present before mapping
    // a product or saving a fitment row.
    fields
        .filter((field) => field.isRequired)
        .every((field) => !!getFitmentFieldValue(values, field));

export const getSelectedFitmentLabel = (fields: FitmentFieldDefinition[], values: FitmentFieldValues): string =>
    fields
        .map((field) => getFitmentFieldValue(values, field))
        .filter(Boolean)
        .join(' ');

export const getLegacyFieldValues = (values: FitmentFieldValues) => ({
    // Shopify product tags still use the old YMM(S) shape when those fields are
    // configured. Custom fields travel separately in fieldValues.
    year: values.year || '',
    make: values.make || '',
    model: values.model || '',
    submodel: values.submodel || '',
});

export const normalizeFitmentFieldValues = (
    fields: FitmentFieldDefinition[],
    values: FitmentFieldValues,
): FitmentFieldValues => {
    const normalized: FitmentFieldValues = {};

    // Drop disabled/hidden fields and trim values before they cross API
    // boundaries. This prevents stale selections after settings changes.
    fields.forEach((field) => {
        const key = normalizeFitmentKey(field);
        const value = (values[key] || '').trim();
        if (key && value) {
            normalized[key] = value;
        }
    });

    const currentKeys = Object.keys(values);
    const normalizedKeys = Object.keys(normalized);
    const isAlreadyNormalized = currentKeys.length === normalizedKeys.length
        && normalizedKeys.every((key) => values[key] === normalized[key]);

    // Preserve object identity when normalization made no changes. Several
    // selector screens normalize state after loading catalog settings; returning
    // the same object prevents an unnecessary second options request.
    return isAlreadyNormalized ? values : normalized;
};

export const updateFitmentFieldValue = (
    fields: FitmentFieldDefinition[],
    values: FitmentFieldValues,
    field: FitmentFieldDefinition,
    nextValue: string,
): FitmentFieldValues => {
    const changedIndex = fields.findIndex((candidate) => normalizeFitmentKey(candidate) === normalizeFitmentKey(field));
    const nextValues: FitmentFieldValues = {};

    // Selector fields are ordered and cascading. When an earlier value changes,
    // later values are cleared so stale downstream selections cannot be submitted.
    fields.forEach((candidate, index) => {
        const key = normalizeFitmentKey(candidate);
        if (!key) return;

        if (index < changedIndex) {
            const existingValue = values[key];
            if (existingValue) nextValues[key] = existingValue;
            return;
        }

        if (index === changedIndex && nextValue) {
            nextValues[key] = nextValue;
        }
    });

    return nextValues;
};

export const resetUnavailableFitmentValues = (
    fields: FitmentFieldDefinition[],
    values: FitmentFieldValues,
): FitmentFieldValues => normalizeFitmentFieldValues(fields, values);

export const isFitmentFieldDisabled = (
    fields: FitmentFieldDefinition[],
    values: FitmentFieldValues,
    field: FitmentFieldDefinition,
    loading: boolean,
): boolean => {
    if (loading) return true;

    const fieldIndex = fields.findIndex((candidate) => normalizeFitmentKey(candidate) === normalizeFitmentKey(field));
    // "Require value" controls add/save validity. "Require before search"
    // controls cascade navigation, so optional searchable fields can stay open.
    return fields
        .slice(0, Math.max(fieldIndex, 0))
        .some((candidate) => candidate.isFilterable && !getFitmentFieldValue(values, candidate));
};

export const getFitmentFieldEmptyOptionLabel = (
    field: FitmentFieldDefinition,
    mode: 'all' | 'select',
): string => {
    const configured = field.helpText?.trim();
    if (configured) return configured;

    const label = field.label.trim() || 'value';
    return mode === 'all' ? `All ${label}` : `Select ${label.toLowerCase()}`;
};
